import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Select, DatePicker, Checkbox, Upload, Button, Input, message } from 'antd';
import { InboxOutlined, FileTextOutlined, DeleteOutlined } from '@ant-design/icons';
import { contracts } from '../api';
import { CONTRACTING_ENTITIES } from './contractingEntities';

dayjs.extend(customParseFormat);

export const ENTITIES = CONTRACTING_ENTITIES;

export const entityName = (seId) =>
  ENTITIES.find((e) => e.value === seId)?.label || '-';

// the API returns contract dates as DD/MM/YYYY
export const parseApiDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, 'DD/MM/YYYY', true);
  return parsed.isValid() ? parsed : dayjs(value);
};

export const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '');

// The API's validators answer with { field: message } objects; notifications need text.
export const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  const raw = data?.message ?? data?.error;
  if (typeof raw === 'string' && raw) return raw;
  if (raw && typeof raw === 'object') {
    const text = Object.values(raw).filter(Boolean).join(' · ');
    if (text) return text;
  }
  return fallback;
};

const MIN_ACTIVATION_DATE = dayjs().subtract(10, 'year');
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_MB = 10;

export const Field = ({ label, required, error, help, extra, children }) => (
  <div className="sd-field">
    {label && (
      <span className="sd-label">
        <span>
          {label}
          {required && <span className="sd-required">*</span>}
        </span>
        {extra}
      </span>
    )}
    {children}
    {error ? <span className="sd-error">{error}</span> : help && <span className="sd-help">{help}</span>}
  </div>
);

export const EntityChoice = ({ value, onChange, disabled }) => (
  <Field label="Contracting entity" required>
    <Select
      size="large"
      style={{ width: '100%' }}
      value={value}
      disabled={disabled}
      onChange={onChange}
      options={ENTITIES.map((entity) => ({
        value: entity.value,
        label: entity.sub ? `${entity.label} (${entity.sub})` : entity.label,
      }))}
    />
  </Field>
);

// Searchable client picker for one Spanidea entity. `currentOption` keeps the
// selected client visible even when it is not in the first page of results.
export const ClientSelect = ({ seId, value, onChange, disabled, currentOption, error }) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      contracts
        .clientNameList(seId === 0 ? 0 : 1, search)
        .then((res) =>
          setOptions(
            (res.data || []).map((c) => ({ value: c.clientId, label: c.legalEntityName }))
          )
        )
        .catch((err) => console.error('Error fetching clients:', err));
    }, 250);
    return () => clearTimeout(timer);
  }, [seId, search]);

  const merged =
    currentOption && !options.some((o) => o.value === currentOption.value)
      ? [currentOption, ...options]
      : options;

  return (
    <Field label="Client" required error={error}>
      <Select
        showSearch
        size="large"
        placeholder="Search for a client"
        value={value}
        onChange={(val, option) => onChange(val, option)}
        onSearch={setSearch}
        filterOption={false}
        options={merged}
        disabled={disabled}
        status={error ? 'error' : ''}
        notFoundContent="No clients found"
      />
    </Field>
  );
};

// Projects of one client. `currentOption` keeps a prefilled project visible.
export const ProjectSelect = ({ clientId, value, onChange, disabled, currentOption, error }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!clientId) {
      setOptions([]);
      return;
    }
    contracts
      .getProjectDropDown(clientId)
      .then((res) =>
        setOptions((res.data || []).map((p) => ({ value: p.projectId, label: p.projectName })))
      )
      .catch((err) => console.error('Error fetching projects:', err));
  }, [clientId]);

  const merged =
    currentOption && !options.some((o) => o.value === currentOption.value)
      ? [currentOption, ...options]
      : options;

  return (
    <Field label="Project" required error={error}>
      <Select
        showSearch
        size="large"
        placeholder={clientId ? 'Choose a project' : 'Choose a client first'}
        value={value}
        onChange={(val, option) => onChange(val, option)}
        optionFilterProp="label"
        options={merged}
        disabled={disabled || !clientId}
        status={error ? 'error' : ''}
        notFoundContent="This client has no projects"
      />
    </Field>
  );
};

export const CURRENCIES = ['₹', '$'];

export const splitAmount = (stored) => {
  const match = String(stored || '').match(/^(\D+)(\d+(?:\.\d{1,2})?)$/);
  return match ? { currency: match[1], amount: match[2] } : { currency: '₹', amount: '' };
};

export const formatAmount = (currency, amount) =>
  amount ? `${currency}${Number(amount).toLocaleString(currency === '₹' ? 'en-IN' : 'en-US')}` : '';

const filterAmount = (raw) => {
  let cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    const [intPart, decPart] = cleaned.split('.');
    cleaned = `${intPart}.${decPart.slice(0, 2)}`;
  }
  return cleaned;
};

export const AmountInput = ({ label, currency, amount, onChange, error, required = true }) => (
  <Field label={label} required={required} error={error}>
    <Input
      size="large"
      inputMode="decimal"
      maxLength={15}
      value={amount}
      status={error ? 'error' : ''}
      placeholder="Amount (e.g. 250.50)"
      onChange={(e) => onChange({ amount: filterAmount(e.target.value) })}
      addonBefore={
        <Select
          value={currency}
          onChange={(value) => onChange({ currency: value })}
          options={CURRENCIES.map((c) => ({ value: c, label: c }))}
          style={{ width: 56 }}
          aria-label="Currency"
        />
      }
    />
  </Field>
);

export const TermFields = ({ start, end, endless, onChange, errors = {}, allowEndless = true }) => (
  <div className="sd-row">
    <Field label="Start date" required error={errors.start}>
      <DatePicker
        size="large"
        format="DD/MM/YYYY"
        value={start}
        allowClear={false}
        inputReadOnly
        status={errors.start ? 'error' : ''}
        disabledDate={(current) => current && current < MIN_ACTIVATION_DATE}
        onChange={(date) =>
          onChange({ start: date, end: end && date && !end.isAfter(date, 'day') ? null : end })
        }
      />
    </Field>
    <Field
      label="End date"
      required={!endless}
      error={errors.end}
      extra={
        allowEndless && (
          <Checkbox
            checked={endless}
            onChange={(e) => onChange({ endless: e.target.checked, end: e.target.checked ? null : end })}
          >
            No end date
          </Checkbox>
        )
      }
    >
      <DatePicker
        size="large"
        format="DD/MM/YYYY"
        value={end}
        allowClear={false}
        inputReadOnly
        disabled={endless}
        status={errors.end ? 'error' : ''}
        disabledDate={(current) => !!start && current && !current.isAfter(start, 'day')}
        onChange={(date) => onChange({ end: date })}
      />
    </Field>
  </div>
);

// file: a File picked now; existingName: the file already stored on the contract
export const FileDrop = ({ file, existingName, onChange, error, label = 'Signed document' }) => {
  const beforeUpload = (picked) => {
    if (!ALLOWED_FILE_TYPES.includes(picked.type)) {
      message.error('Upload a PDF or Word document.');
      return Upload.LIST_IGNORE;
    }
    if (picked.size / 1024 / 1024 >= MAX_FILE_MB) {
      message.error(`The file must be smaller than ${MAX_FILE_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    onChange(picked);
    return false;
  };

  const shownName = file?.name || existingName;

  return (
    <Field label={label} required error={error}>
      {shownName ? (
        <div className="sd-file">
          <FileTextOutlined style={{ fontSize: 20, color: '#475467' }} />
          <span className="sd-file-name">{shownName}</span>
          {file ? (
            <Button type="text" aria-label="Remove file" icon={<DeleteOutlined />} onClick={() => onChange(null)} />
          ) : (
            <Upload beforeUpload={beforeUpload} showUploadList={false} accept=".pdf,.doc,.docx">
              <Button>Replace</Button>
            </Upload>
          )}
        </div>
      ) : (
        <Upload.Dragger
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept=".pdf,.doc,.docx"
          style={error ? { borderColor: '#D92D20' } : undefined}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop the file here, or click to browse</p>
          <p className="ant-upload-hint">PDF or Word, up to {MAX_FILE_MB} MB</p>
        </Upload.Dragger>
      )}
    </Field>
  );
};

export const Summary = ({ rows }) => (
  <div className="sd-summary">
    {rows.map((row) => (
      <div key={row.label} className="sd-summary-row">
        <span className="sd-summary-key">{row.label}</span>
        {row.value ? (
          <span className="sd-summary-value">{row.value}</span>
        ) : (
          <span className="sd-summary-missing">{row.missing || 'Missing'}</span>
        )}
      </div>
    ))}
  </div>
);
